// src/pages/TrainingManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Button, Chip, LinearProgress, Tabs, Tab,
  List, ListItem, ListItemText, ListItemIcon,
  Avatar, IconButton, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import {
  School, Assignment, Quiz, Verified,
  PlayArrow, Schedule, People, TrendingUp
} from '@mui/icons-material';
import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

const TrainingManagement = () => {
  const { t } = useI18n();
  const [trainings, setTrainings] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadTrainings();
  }, []);

  const loadTrainings = async () => {
    const trainingsData = await getData('trainings') || getDefaultTrainings();
    setTrainings(trainingsData);
  };

  const getDefaultTrainings = () => [
    {
      id: 1,
      title_key: 'training.chemical_safety_title',
      desc_key: 'training.chemical_safety_desc',
      category: 'safety',
      duration: '2 hours',
      difficulty: 'training.beginner',
      completed: 45,
      total: 120,
      status: 'active'
    },
    {
      id: 2,
      title_key: 'training.emergency_protocol_title',
      desc_key: 'training.emergency_protocol_desc', 
      category: 'safety',
      duration: '1.5 hours',
      difficulty: 'training.intermediate',
      completed: 30,
      total: 120,
      status: 'active'
    },
    {
      id: 3,
      title_key: 'training.waste_sorting_title',
      desc_key: 'training.waste_sorting_desc',
      category: 'environment',
      duration: '1 hour',
      difficulty: 'training.beginner',
      completed: 85,
      total: 120,
      status: 'active'
    },
    {
      id: 4,
      title_key: 'training.machine_operation_title',
      desc_key: 'training.machine_operation_desc',
      category: 'equipment',
      duration: '3 hours',
      difficulty: 'training.intermediate', 
      completed: 20,
      total: 120,
      status: 'active'
    },
    {
      id: 5,
      title_key: 'training.quality_control_title',
      desc_key: 'training.quality_control_desc',
      category: 'quality',
      duration: '2.5 hours',
      difficulty: 'training.advanced',
      completed: 15,
      total: 120,
      status: 'active'
    }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('training.title')}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3 }}>
            <School sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {trainings.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('training.total_courses')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3 }}>
            <People sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {trainings.reduce((sum, t) => sum + t.completed, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('training.employees_trained')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3 }}>
            <Verified sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {Math.round(trainings.reduce((sum, t) => sum + (t.completed / t.total), 0) / trainings.length * 100)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('training.completion_rate')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3 }}>
            <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {trainings.filter(t => t.status === 'active').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('training.active_courses')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {trainings.map((training) => (
          <Grid item xs={12} sm={6} lg={4} key={training.id}>
            <TrainingCard training={training} t={t} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const TrainingCard = ({ training, t }) => {
  const progress = (training.completed / training.total) * 100;
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <School />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t(training.title_key)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {training.duration} • {t(training.difficulty)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(training.desc_key)}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('training.progress')}
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip 
            label={t(`training.${training.category}`)} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
          <Chip 
            label={`${training.completed}/${training.total}`}
            size="small"
            icon={<People />}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default TrainingManagement;